import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { userApi, businessDomainApi, businessScenarioApi } from '../services/api';

// 角色权限级别常量
const ROLE_LEVELS = {
  NONE: 0,           // 无权限
  DOMAIN_MEMBER: 1,   // 项目成员（只读权限）
  SCENARIO_ADMIN: 2,  // 业务场景管理员
  DOMAIN_ADMIN: 3,    // 项目空间管理员
};

const WorkspaceContext = createContext();

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
};

export const WorkspaceProvider = ({ children }) => {
  // 从 localStorage 恢复状态
  const [currentDomain, setCurrentDomain] = useState(() => {
    const saved = localStorage.getItem('currentDomain');
    return saved ? JSON.parse(saved) : null;
  });

  const [currentScenario, setCurrentScenario] = useState(() => {
    const saved = localStorage.getItem('currentScenario');
    return saved ? JSON.parse(saved) : null;
  });

  // 权限相关状态
  const [userRole, setUserRole] = useState(ROLE_LEVELS.NONE);
  const [roleLoading, setRoleLoading] = useState(false);

  // 保存到 localStorage
  useEffect(() => {
    if (currentDomain) {
      localStorage.setItem('currentDomain', JSON.stringify(currentDomain));
    } else {
      localStorage.removeItem('currentDomain');
    }
  }, [currentDomain]);

  useEffect(() => {
    if (currentScenario) {
      localStorage.setItem('currentScenario', JSON.stringify(currentScenario));
    } else {
      localStorage.removeItem('currentScenario');
    }
  }, [currentScenario]);

  // 获取用户角色
  const fetchUserRole = useCallback(async (domainId, scenarioId) => {
    if (!domainId) {
      setUserRole(ROLE_LEVELS.NONE);
      return;
    }

    setRoleLoading(true);
    try {
      const response = await userApi.getUserRole(domainId, scenarioId);
      // 响应格式： { roleLevel: 0|1|2|3, roleName: 'xxx' }
      const roleLevel = typeof response === 'number' ? response : (response?.roleLevel ?? ROLE_LEVELS.NONE);
      setUserRole(roleLevel);
    } catch (error) {
      console.error('获取用户角色失败:', error);
      setUserRole(ROLE_LEVELS.NONE);
    } finally {
      setRoleLoading(false);
    }
  }, []);

  // 当项目空间或业务场景变化时，重新获取用户角色
  useEffect(() => {
    if (currentDomain?.id) {
      fetchUserRole(currentDomain.id, currentScenario?.id);
    } else {
      setUserRole(ROLE_LEVELS.NONE);
    }
  }, [currentDomain?.id, currentScenario?.id, fetchUserRole]);

  // 进入项目空间
  const enterDomain = (domain) => {
    setCurrentDomain(domain);
    setCurrentScenario(null); // 切换项目空间时清空业务场景
  };

  // 退出项目空间
  const exitDomain = () => {
    setCurrentDomain(null);
    setCurrentScenario(null);
  };

  // 进入业务场景
  const enterScenario = (scenario) => {
    setCurrentScenario(scenario);
  };

  // 退出业务场景
  const exitScenario = () => {
    setCurrentScenario(null);
  };

  /**
   * 检查用户是否有管理权限（项目空间管理员 或 业务场景管理员）
   * @returns {boolean} 是否有管理权限
   */
  const hasManagePermission = useCallback(() => {
    return userRole >= ROLE_LEVELS.SCENARIO_ADMIN;
  }, [userRole]);

  /**
   * 检查用户是否是项目空间管理员
   * @returns {boolean} 是否是项目空间管理员
   */
  const isDomainAdmin = useCallback(() => {
    return userRole === ROLE_LEVELS.DOMAIN_ADMIN;
  }, [userRole]);

  /**
   * 检查用户是否是业务场景管理员
   * @returns {boolean} 是否是业务场景管理员
   */
  const isScenarioAdmin = useCallback(() => {
    return userRole === ROLE_LEVELS.SCENARIO_ADMIN;
  }, [userRole]);

  /**
   * 检查用户是否仅为项目成员（只读权限）
   * @returns {boolean} 是否仅为项目成员
   */
  const isMemberOnly = useCallback(() => {
    return userRole === ROLE_LEVELS.DOMAIN_MEMBER;
  }, [userRole]);

  /**
   * 从 URL 参数初始化项目空间和业务场景
   * @param {number} domainId 项目空间ID
   * @param {number} scenarioId 业务场景ID
   * @returns {Promise<boolean>} 是否初始化成功
   */
  const initFromUrlParams = useCallback(async (domainId, scenarioId) => {
    try {
      // 如果已有相同的 domain 和 scenario，跳过
      if (currentDomain?.id === domainId && currentScenario?.id === scenarioId) {
        return true;
      }

      // 获取项目空间详情
      const domainDetail = await businessDomainApi.getDetail(domainId);
      if (!domainDetail) {
        console.error('项目空间不存在:', domainId);
        return false;
      }

      // 获取业务场景详情
      const scenarioDetail = await businessScenarioApi.getDetail(scenarioId);
      if (!scenarioDetail) {
        console.error('业务场景不存在:', scenarioId);
        return false;
      }

      // 设置当前项目空间和业务场景
      setCurrentDomain(domainDetail);
      setCurrentScenario(scenarioDetail);

      return true;
    } catch (error) {
      console.error('从URL参数初始化失败:', error);
      return false;
    }
  }, [currentDomain, currentScenario]);

  const value = {
    currentDomain,
    currentScenario,
    enterDomain,
    exitDomain,
    enterScenario,
    exitScenario,
    initFromUrlParams,
    // 权限相关
    userRole,
    roleLoading,
    hasManagePermission,
    isDomainAdmin,
    isScenarioAdmin,
    isMemberOnly,
    refreshUserRole: () => fetchUserRole(currentDomain?.id, currentScenario?.id),
    ROLE_LEVELS,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};
